/**
 * Swipe maths (Phase 171) — the DOM-free half of `useSwipe`.
 *
 * "Did that count as a swipe" is decided here so it can be asserted without a
 * pointer. Distance OR velocity commits: a slow deliberate drag past the
 * threshold, or a quick flick that covers at least half of it.
 */

export const SWIPE_THRESHOLD_PX = 64;
/** px per ms — 500 px/s, roughly a casual flick. */
export const SWIPE_VELOCITY_PX_MS = 0.5;

export function shouldCommitSwipe(
  delta: number,
  elapsedMs: number,
  threshold = SWIPE_THRESHOLD_PX,
): boolean {
  const distance = Math.abs(delta);
  if (distance >= threshold) return true;
  if (distance < threshold / 2) return false;
  const velocity = elapsedMs > 0 ? distance / elapsedMs : Number.POSITIVE_INFINITY;
  return velocity >= SWIPE_VELOCITY_PX_MS;
}

/**
 * Diminishing motion past `limit` — the element keeps following the finger,
 * but ever more reluctantly, so pulling the wrong way reads as "resisting"
 * rather than "stuck".
 */
export function rubberBand(delta: number, limit: number): number {
  const sign = Math.sign(delta);
  const distance = Math.abs(delta);
  if (distance <= limit) return delta;
  return sign * (limit + (distance - limit) * 0.3);
}

/**
 * Which resting height should a sheet settle at after a drag?
 *
 * `snaps` are ascending fractions of the viewport; `height` is the current
 * fraction. Below `dismissBelow` the answer is null — close the sheet.
 */
export function nearestSnap(
  height: number,
  snaps: readonly number[],
  dismissBelow: number,
): number | null {
  if (height < dismissBelow) return null;
  let best: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const snap of snaps) {
    const distance = Math.abs(snap - height);
    if (distance < bestDistance) {
      best = snap;
      bestDistance = distance;
    }
  }
  return best;
}
